import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Store } from 'src/app/core/services/store.service';
import { CartState, CheckedLiability } from '../models/cart-state.model';
import { Payment } from '../../pay/models/payment.model';
import { PaymentService } from '../../pay/services/payment.service';
import { AuthStoreService } from './auth/auth-store.service';
import { Liability } from '../../liabilities/models/liability.model';
import { CheckedLiabilitiesStoreService } from './checked-liabilities.service';

@Injectable({
  providedIn: 'root',
})
export class CartStoreService extends Store<CartState> implements OnDestroy {

  private checkedList$: Subscription;

  private paymentObserver$: Subscription;
  private removingIds$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private addedIds$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  public removingIdsFromCart$: Observable<string[]> = this.removingIds$.asObservable();
  public addedIdsToCart$: Observable<string[]> = this.addedIds$.asObservable();

  constructor(private paymentService: PaymentService,
              public authStoreService: AuthStoreService,
              private checkedLiabilitiesStore: CheckedLiabilitiesStoreService,
  ) {
    super({
      totalAmount: 0,
      counter: null,
      paymentFragments: [],
      newAmount: 0,
      paymentId: null,
      checkedList: null,
    }, 'CartStore');
    let state: CartState = new CartState();
    state.checkedList = this.checkedLiabilitiesStore.state;

    this.paymentObserver$ = this.paymentService.basePayment$
      .pipe(
        switchMap((basePayment) => {

          if (basePayment?.id) {
            state.paymentId = basePayment.id;
            return this.paymentService.getPayment(basePayment.id);
          }
          state.paymentId = null;
          state.counter = 0;
          state.totalAmount = 0;
          this.setState(state);
          return of({});
        }),
        tap((payment: Payment) => {

          let removed;
          let added;
          let newPaymentFragments = payment?.paymentFragments?.map(value => value.liability);

          if (this.state.paymentFragments) {
            if (newPaymentFragments) {
              removed = this.state.paymentFragments.map(value => value.id)
                .filter(value => !newPaymentFragments.map(newPaymentFragment => newPaymentFragment.id).includes(value));
              added = newPaymentFragments.map(value => value.id)
                .filter(value => !this.state.paymentFragments.map(pf => pf.id).includes(value));

            } else {
              removed = this.state.paymentFragments.map(value => value.id);
            }
          } else {
            if (newPaymentFragments) {
              added = newPaymentFragments.map(value => value.id);
            }
          }
          state.counter = payment?.paymentFragments?.length;
          state.totalAmount = payment.liabilitiesAmountToPaySum ? payment.liabilitiesAmountToPaySum : 0;
          state.paymentFragments = newPaymentFragments;
          if (state.checkedList && state.checkedList.checkedLiabilities) {
            let newList = state.checkedList.checkedLiabilities.filter(value => !state.paymentFragments ||
              !state.paymentFragments.map(liability => liability.id).includes(value.id));
            state.checkedList = {
              checkedLiabilities: newList,
              checkedLiabilitiesAmount: this.sumLiabilities(newList),
            };
          } else {
            state.checkedList = {
              checkedLiabilities: [],
              checkedLiabilitiesAmount: 0,
            };
          }

          state.newAmount = state.checkedList.checkedLiabilitiesAmount + state.totalAmount;
          if (removed && removed.length > 0) {
            this.removingIds$.next([...removed]);
          }
          if (added && added.length > 0) {
            this.addedIds$.next([...added]);
          }
          this.checkedLiabilitiesStore.setState(state.checkedList);
          this.setState(state);
        }),
      )
      .subscribe(
        () => {
          this.checkedList$ = this.checkedLiabilitiesStore.state$.subscribe(
            value => {
              this.state.newAmount = this.state.totalAmount
                + (value && value.checkedLiabilitiesAmount ? value.checkedLiabilitiesAmount : 0);
              this.state.checkedList = value;
              this.setState(this.state);
            },
          );
        },
      );
    this.authStoreService.logout$.subscribe(() => this.resetCartState());
    this.authStoreService.login$.subscribe(() => this.paymentService.refreshBasePayment());


  }

  ngOnDestroy(): void {
    this.paymentObserver$.unsubscribe();
  }

  resetCartState() {
    this.setState(new CartState());
  }


  remove(id: string) {
    this.checkedLiabilitiesStore.remove(id);
  }

  removeId(id: string){
    this.removingIds$.next([id]);
  }

  removeList(ids: string[]) {
    this.checkedLiabilitiesStore.removeList(ids);
  }

  addCheckedLiabilityList(liabilities: CheckedLiability[]) {
    this.checkedLiabilitiesStore.addCheckedLiabilityList(liabilities);
  }

  addCheckedLiability(checkedLiability: CheckedLiability) {
    this.checkedLiabilitiesStore.addCheckedLiability(checkedLiability);
  }

  addLiability(liability: Liability) {
    this.checkedLiabilitiesStore.addLiability(liability);
  }

  addLiabilityList(liabilities: Liability[]) {
    this.checkedLiabilitiesStore.addLiabilityList(liabilities);
  }

  public getIds() {
    return this.checkedLiabilitiesStore.getIds();
  }

  public clearCheckedLiabilities() {
    return this.checkedLiabilitiesStore.clearCheckedLiabilities();
  }

  public sumLiabilities(liabilities: CheckedLiability[]) {
    if (!liabilities) {
      return 0;
    } else {
      return liabilities.reduce((sum, currentValue) => sum + currentValue.amountToPay, 0);

    }
  }

}
